import type { ReactNode } from "react";
import { pluralize } from "@common/helpers/string";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { HStack } from "@design-system/HStack/HStack";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ArrowCornerIcon } from "@icons";
import type { GroupBy } from "./useGroupedTableData";
import { isWorkPersonRole } from "./workPersonGroups";

interface ClientsWorkTotalArgs {
    groupBy: GroupBy;
    applicationCount: number;
    templateCount: number;
    clientCount: number;
}

interface ClientsWorkTotal {
    label: string;
    count: number;
}

interface ClientsWorkFooterProps {
    total: ClientsWorkTotal;
    /** The layout's page controls: List's DataGrid pager, Board's own. */
    pager: ReactNode;
    selectedCount: number;
    onDeselect: () => void;
    onOpenBulkActions: () => void;
    /** View in Actions for the work item whose details are open. */
    viewInActionsLink: string | undefined;
}

/** What the island totals for the grouping: work items, templates or clients. */
export const getClientsWorkTotal = ({
    groupBy,
    applicationCount,
    templateCount,
    clientCount,
}: ClientsWorkTotalArgs): ClientsWorkTotal => {
    if (
        groupBy === "none" ||
        groupBy === "stage" ||
        isWorkPersonRole(groupBy)
    ) {
        return {
            label: pluralize("Work item", applicationCount),
            count: applicationCount,
        };
    }
    if (groupBy === "template") {
        return {
            label: pluralize("Template", templateCount),
            count: templateCount,
        };
    }
    return { label: pluralize("Client", clientCount), count: clientCount };
};

/**
 * The Work page's command island: pagination, the total for the grouping,
 * and the selection with its bulk actions. With nothing selected and a work
 * item's details open, it offers View in Actions for that item instead.
 */
export const ClientsWorkFooter = ({
    total: { label, count },
    pager,
    selectedCount,
    onDeselect,
    onOpenBulkActions,
    viewInActionsLink,
}: Readonly<ClientsWorkFooterProps>) => (
    <CommandIsland
        sticky
        className={commandIslandStyles.bulkActionsStickyFooter}
    >
        <HStack gap={8}>
            {pager}

            <CommandIslandTotal>
                {label}: {count}
            </CommandIslandTotal>

            {selectedCount > 0 && (
                <Tooltip
                    key={selectedCount}
                    content={`Deselect ${selectedCount}`}
                >
                    <Button size="md" variant="primary" onClick={onDeselect}>
                        {selectedCount} selected
                    </Button>
                </Tooltip>
            )}
        </HStack>

        {selectedCount > 0 && (
            <Tooltip content="Open bulk actions">
                <Button
                    variant="primary"
                    size="md"
                    onClick={onOpenBulkActions}
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="mod+k" />
                        </span>
                    }
                >
                    Actions
                </Button>
            </Tooltip>
        )}

        {selectedCount === 0 && viewInActionsLink && (
            <Button
                variant="primary"
                size="md"
                asChild
                rightIcon={<ArrowCornerIcon />}
            >
                <StateAwareLink to={viewInActionsLink}>
                    View in Actions
                </StateAwareLink>
            </Button>
        )}
    </CommandIsland>
);
